/**
 * GlobalLooseRelationshipEditorDialog
 *
 * App-level mount of `LooseRelationshipEditorDialog` driven by
 * `looseRelationshipEditorDialogStore`. Lets any surface launch the editor
 * for a loose FK (grid header FK glyph, MetaInfo manager Details button,
 * settings Details button, etc.) without prop-drilling.
 *
 * Resolves `existingRelationshipId` against the live MetaInfo so the picker
 * always opens with fresh `initialTarget` / `initialSourceFilter` /
 * `initialTargetFilter` / `initialSourcePath` values.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLooseRelationshipEditorDialogStore } from './looseRelationshipEditorDialogStore';
import { LooseRelationshipEditorDialog } from './LooseRelationshipEditorDialog';
import { useAvailableTables } from '../../../table-explorer/hooks/useAvailableTables';
import { useMetaInfo } from '../../hooks/useMetaInfo';
import type { InferTable } from '../../utils/inferLooseRelationship';
import { hostMetadataQueryKey } from '../../../table-explorer/components/tableExplorerHostPorts';

export function GlobalLooseRelationshipEditorDialog() {
  const { isOpen, context, closeDialog } = useLooseRelationshipEditorDialogStore();

  const tablesQuery = useAvailableTables({
    connectionId: context?.connectionId ?? '',
    database: context?.database ?? '',
    engineId: context?.engine,
    enabled: isOpen && !!context && !context.metadataEditors,
  });
  const hostTablesQuery = useQuery({
    queryKey: context?.metadataEditors
      ? hostMetadataQueryKey(context.metadataEditors, context.connectionId, 'tables', context.database)
      : ['host-metadata-tables', context?.connectionId, context?.database],
    queryFn: () => context!.metadataEditors!.listTables({
      connectionId: context!.connectionId,
      database: context!.database,
    }),
    enabled: isOpen && !!context?.metadataEditors,
    staleTime: 5 * 60 * 1000,
  });
  const allTables = useMemo<InferTable[]>(
    () =>
      ((context?.metadataEditors ? hostTablesQuery.data : tablesQuery.data) ?? []).map((t) => ({
        schema: t.schema,
        name: t.name,
        columns: [] as { name: string; dataType: string }[],
      })),
    [tablesQuery.data, hostTablesQuery.data, context?.metadataEditors],
  );

  const metaInfoQuery = useMetaInfo(
    context?.connectionId ?? null,
    context?.database ?? null,
  );
  const looseRel = useMemo(() => {
    if (!context?.existingRelationshipId) return undefined;
    return (metaInfoQuery.data?.looseRelationships ?? []).find(
      (r) => r.id === context.existingRelationshipId,
    );
  }, [metaInfoQuery.data, context?.existingRelationshipId]);

  if (!isOpen || !context) return null;

  return (
    <LooseRelationshipEditorDialog
      open
      onOpenChange={(o) => {
        if (!o) closeDialog();
      }}
      connectionId={context.connectionId}
      database={context.database}
      engine={context.engine}
      source={context.source}
      allTables={allTables}
      existingRelationshipId={
        context.existingRelationshipId && looseRel ? looseRel.id : undefined
      }
      initialTarget={
        looseRel
          ? {
              schema: looseRel.toSchema,
              table: looseRel.toTable,
              column: looseRel.toColumn,
            }
          : undefined
      }
      initialSourceFilter={looseRel?.sourceFilter}
      initialTargetFilter={looseRel?.targetFilter}
      initialSourcePath={looseRel?.sourcePath ?? context.initialSourcePath}
      addedSource={context.source_label ?? 'global_dialog'}
      metadataEditors={context.metadataEditors}
    />
  );
}
